<template>
  <header
    :class="[
      'fixed top-0 z-sidebar transition-[left,right] duration-200',
      'bg-theme-surface-raised/80 backdrop-blur-md',
      'h-16 flex items-center gap-3 border-b border-theme-border',
      'px-4 sm:px-6',
      isMobileView ? 'left-0 right-0' : isSidebarCollapsed ? 'left-16 right-0' : 'left-72 right-0',
    ]"
  >
    <!-- Hamburger menu (mobile only) -->
    <AppIconButton
      v-if="isMobileView"
      variant="ghost"
      tone="neutral"
      size="lg"
      @click="toggleSidebar"
      :aria-label="t('sidebar.aria.toggle')"
    >
      <icon :path="mdiMenu" size="22" />
    </AppIconButton>

    <!-- Left: Global search.
         PI-244/246 design discards breadcrumbs and the contextual page title:
         every page now carries its own in-page header (eyebrow + icon + title
         + subtitle + favoritable star) via `StandardToolPage` / `PageLayout`,
         so the chrome only surfaces the global search on the left and a
         compact role + avatar cluster on the right. -->
    <div class="flex-1 min-w-0 flex items-center gap-2 max-w-2xl">
      <span v-if="demoEnabled && !isAuthenticated" class="text-sm font-semibold text-theme-brand">{{ t('portal_demo.header_label') }}</span>
      <div v-if="isAuthenticated && !route.meta?.hideGlobalSearch && !isApiOnly" class="hidden md:block w-full">
        <HomeGlobalSearch />
      </div>
    </div>

    <div v-if="!isAuthenticated" class="ml-auto flex shrink-0 items-center gap-3">
      <AppButton v-if="demoEnabled" to="/subscriptions?intent=platform" :label="t('portal_demo.plans')" variant="solid" tone="primary" size="sm" data-test="demo-subscribe" />
      <router-link to="/login" class="text-sm font-medium text-theme-palette-emerald" data-test="demo-login">{{ t('menu_login') }}</router-link>
    </div>

    <!-- Right: grouped notifications + profile. -->
    <div v-if="isAuthenticated" class="ml-auto flex items-center gap-3 shrink-0">
      <div
        v-if="isAuthenticated"
        class="inline-flex h-10 items-stretch overflow-visible rounded-theme-lg border border-theme-border bg-theme-surface shadow-[var(--shadow-control)]
          dark:border-theme-border dark:bg-theme-surface dark:shadow-theme-shadow/30"
      >
        <NotificationBell
          class="flex self-stretch"
          :button-class="[
            'relative flex h-full w-10 items-center justify-center rounded-l-theme-lg rounded-r-none text-theme-text-secondary transition-colors',
            'hover:bg-theme-bg-subtle dark:hover:bg-theme-surface-raised',
          ]"
        />

        <div class="my-2 w-px bg-theme-border-strong dark:bg-theme-border"></div>

        <!-- Avatar Dropdown -->
        <div class="relative" ref="dropdownRef">
          <button
            type="button"
            @click="toggleDropdown"
            :aria-expanded="isDropdownOpen ? 'true' : 'false'"
            aria-haspopup="menu"
            class="flex h-full items-center gap-2 rounded-r-theme-lg px-2.5 py-1.5 transition-colors
              hover:bg-theme-bg-subtle dark:hover:bg-theme-surface-raised"
          >
            <div
              class="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-theme-palette-emerald to-theme-brand-hover
                text-xs font-semibold text-theme-text-inverse shadow-theme-glow-primary"
            >
              {{ userInitials }}
            </div>
            <div class="hidden min-w-0 pt-1 text-left sm:flex sm:flex-col sm:gap-0">
              <p class="max-w-[10rem] truncate font-azonix text-xs uppercase leading-3 tracking-wide text-theme-text">
                {{ userName }}
              </p>
              <p class="max-w-[10rem] truncate text-xs leading-3.5 text-theme-palette-emerald dark:text-theme-palette-emerald">
                {{ currentRoleLabel }}
              </p>
            </div>
            <icon
              :path="isDropdownOpen ? mdiChevronUp : mdiChevronDown"
              size="16"
              class="shrink-0 text-theme-text-muted"
            />
          </button>

          <!-- Dropdown Menu -->
          <transition name="dropdown">
            <div
              v-if="isDropdownOpen"
              class="absolute right-0 z-50 mt-3 w-72 rounded-theme-lg border border-theme-border bg-theme-surface py-2 shadow-theme-dialog dark:shadow-theme-shadow/60
                dark:border-theme-border dark:bg-theme-surface-elevated"
              role="menu"
            >
              <!-- User Info Section -->
              <div class="px-4 py-3 border-b border-theme-border dark:border-theme-border">
                <p class="truncate text-sm font-medium text-theme-text">{{ userName }}</p>
                <p class="truncate text-xs text-theme-text-secondary">{{ userEmail }}</p>
                <p class="mt-1 truncate text-xs font-medium text-theme-palette-emerald dark:text-theme-palette-emerald">
                  {{ currentRoleLabel }}
                </p>
              </div>

              <!-- Profile -->
              <router-link
                to="/profile"
                @click="closeDropdown"
                class="flex items-center gap-3 px-4 py-2.5 text-sm text-theme-text-secondary hover:bg-theme-bg-subtle dark:hover:bg-theme-surface-raised transition-colors"
              >
                <icon :path="mdiAccount" size="18" class="text-theme-text-muted shrink-0" />
                <span>{{ t('menu_profile') }}</span>
              </router-link>

              <!-- What's new -->
              <button
                type="button"
                role="menuitem"
                :aria-expanded="isChangelogOpen"
                @click="toggleChangelog(); closeDropdown()"
                data-test="changelog-link"
                class="flex w-full items-center gap-3 px-4 py-2.5 text-left text-sm text-theme-text-secondary hover:bg-theme-bg-subtle dark:hover:bg-theme-surface-raised transition-colors"
              >
                <icon :path="mdiBullhornOutline" size="18" class="text-theme-text-muted shrink-0" />
                <span>{{ t('changelog.nav') }}</span>
                <span
                  v-if="changelogUnread"
                  class="ml-auto h-2 w-2 shrink-0 rounded-full bg-theme-palette-emerald"
                  data-test="changelog-unread-dot"
                  :aria-label="t('changelog.nav')"
                ></span>
              </button>

            <!-- Subscription -->
            <template v-if="hasOrHadSubscription">
              <router-link
                :to="isApiOnly ? '/api/access' : { name: 'subscription.upgrade' }"
                @click="closeDropdown"
                class="flex items-center gap-3 px-4 py-2.5 text-sm text-theme-text-secondary hover:bg-theme-bg-subtle dark:hover:bg-theme-surface-raised transition-colors"
              >
                <icon :path="mdiCreditCard" size="18" class="text-theme-text-muted shrink-0" />
                <span>{{ t('menu_my_subscription') }}</span>
              </router-link>
            </template>

            <!-- i18n Inspector -->
            <AppButton
              v-if="isSuperUser"
              @click="handleToggleInspect"
              variant="ghost"
              tone="neutral"
              block
              justify="start"
              :class="[
                'gap-3 px-4 py-2.5 text-sm',
                isInspectMode
                  ? 'text-theme-palette-amber dark:text-theme-palette-amber bg-theme-palette-amber/10 dark:bg-theme-palette-amber/20'
                  : 'text-theme-text-secondary hover:bg-theme-bg-subtle dark:hover:bg-theme-surface-raised',
              ]"
            >
              <icon
                :path="mdiTranslate"
                size="18"
                :class="['shrink-0', isInspectMode ? 'text-theme-palette-amber' : 'text-theme-text-muted']"
              />
              <span>{{ t('menu_i18n_inspect') }}</span>
            </AppButton>

            <div class="border-t border-theme-border dark:border-theme-border my-2"></div>

            <!-- Language Selector -->
            <AppButton
              @click="toggleLanguageSubmenu"
              variant="ghost"
              tone="neutral"
              block
              justify="between"
              class="gap-3 px-4 py-2.5 text-sm text-theme-text-secondary hover:bg-theme-bg-subtle dark:hover:bg-theme-surface-raised"
            >
              <div class="flex items-center gap-3">
                <span class="text-lg leading-none">{{ currentFlag }}</span>
                <span>{{ currentLanguageName }}</span>
              </div>
              <icon
                :path="isLanguageSubmenuOpen ? mdiChevronUp : mdiChevronDown"
                size="16"
                class="text-theme-text-muted shrink-0"
              />
            </AppButton>

            <!-- Language Submenu -->
            <transition name="submenu">
              <div v-if="isLanguageSubmenuOpen" class="bg-theme-bg-subtle dark:bg-theme-surface-raised">
                <AppButton
                  v-for="lang in availableLanguages"
                  :key="lang.code"
                  @click="setLocale(lang.code)"
                  variant="ghost"
                  tone="neutral"
                  block
                  justify="start"
                  :class="[
                    'gap-3 px-4 py-2 text-sm',
                    locale === lang.code
                      ? 'text-theme-palette-emerald dark:text-theme-palette-emerald bg-theme-palette-emerald/10 dark:bg-theme-palette-emerald/30'
                      : 'text-theme-text-secondary hover:bg-theme-bg-subtle dark:hover:bg-theme-surface-raised',
                  ]"
                >
                  <span class="text-base leading-none ml-9">{{ lang.flag }}</span>
                  <span>{{ lang.name }}</span>
                </AppButton>
              </div>
            </transition>

            <!-- Dark Mode Toggle -->
            <AppButton
              @click="toggleDarkMode"
              variant="ghost"
              tone="neutral"
              block
              justify="between"
              class="gap-3 px-4 py-2.5 text-sm text-theme-text-secondary hover:bg-theme-bg-subtle dark:hover:bg-theme-surface-raised"
            >
              <div class="flex items-center gap-3">
                <div class="w-5 h-5 flex items-center justify-center shrink-0">
                  <transition name="icon-swap" mode="out-in">
                    <icon
                      v-if="isDarkMode"
                      key="sun"
                      :path="mdiWeatherSunny"
                      size="18"
                      class="text-theme-palette-amber"
                    />
                    <icon
                      v-else
                      key="moon"
                      :path="mdiWeatherNight"
                      size="18"
                      class="text-theme-text-secondary"
                    />
                  </transition>
                </div>
                <span>{{ t('menu_darkmode') }}</span>
              </div>
              <div
                :class="[
                  'relative w-11 h-6 rounded-full transition-colors duration-300 ease-in-out shrink-0',
                  isDarkMode ? 'bg-theme-palette-emerald' : 'bg-theme-surface-raised dark:bg-theme-surface-raised',
                ]"
              >
                <div
                  :class="[
                    'absolute top-0.5 w-5 h-5 bg-theme-surface rounded-full shadow-sm transform transition-transform duration-300 ease-in-out',
                    isDarkMode ? 'translate-x-5' : 'translate-x-0.5',
                  ]"
                ></div>
              </div>
            </AppButton>

            <!-- Report a bug (internal users only) -->
            <AppButton
              v-if="showBugReportButton"
              @click="openBugReportForm"
              variant="ghost"
              tone="neutral"
              block
              justify="start"
              class="gap-3 px-4 py-2.5 text-sm text-theme-text-secondary hover:bg-theme-bg-subtle dark:hover:bg-theme-surface-raised"
            >
              <icon :path="mdiBugOutline" size="18" class="text-theme-text-muted shrink-0" />
              <span>{{ t('common.report_bug') }}</span>
            </AppButton>

            <div class="border-t border-theme-border dark:border-theme-border my-2"></div>

            <!-- Logout -->
            <AppMenuItem tone="danger" @click="logout">
              <icon :path="mdiLogout" size="18" class="shrink-0" />
              <span>{{ t('menu_logout') }}</span>
            </AppMenuItem>
          </div>
        </transition>
      </div>
    </div>
    </div>
  </header>

</template>

<script setup>
  import { demoEnabled } from '@/storage/demoMode';
  import AppButton from '@/components/ui/buttons/AppButton.vue';
  import AppIconButton from '@/components/ui/buttons/AppIconButton.vue';
  import AppMenuItem from '@/components/ui/nav/AppMenuItem.vue';
  import HomeGlobalSearch from '@/pages/home/components/HomeGlobalSearch.vue';
  import Icon from '@/components/Icon';
  import NotificationBell from '@/features/notifications/components/NotificationBell.vue';
  import { useI18nInspector } from '@/composables/useI18nInspector';
  import { useChangelog } from '@/features/changelog/composables/useChangelog';
  import { useProfile } from '@/composables/profile';
  import { useDisplayStore } from '@/stores/useDisplayStore';
  import {
    mdiAccount,
    mdiBullhornOutline,
    mdiBugOutline,
    mdiChevronDown,
    mdiChevronUp,
    mdiCreditCard,
    mdiLogout,
    mdiMenu,
    mdiTranslate,
    mdiWeatherNight,
    mdiWeatherSunny,
  } from '@mdi/js';
  import { computed, onMounted, onUnmounted, ref } from 'vue';
  import { useI18n } from 'vue-i18n';
  import { useRoute } from 'vue-router';
  import { useAuthStore } from '@/stores/useAuthStore';
  import { redirectToLoginPage } from '@/utils/authRedirect';

  const route = useRoute();
  const authStore = useAuthStore();
  const displayStore = useDisplayStore();
  // The API portal has no tools or dashboards to search.
  const isApiOnly = computed(() => authStore.isApiOnly);
  const { t, locale } = useI18n();
  const { profile, rolePresets } = useProfile();
  const { unread: changelogUnread, isSidebarOpen: isChangelogOpen, toggleChangelog } = useChangelog();

  // Dropdown state
  const isDropdownOpen = ref(false);
  const isLanguageSubmenuOpen = ref(false);
  const dropdownRef = ref(null);

  // Mobile detection
  const isMobileView = ref(false);

  // Sidebar state
  const isSidebarCollapsed = computed(() => displayStore.isSidebarCollapsed);

  // User state
  const isAuthenticated = computed(() => authStore.isAuthenticated);
  const userEmail = computed(() => authStore.email || '');
  const userName = computed(() => {
    if (authStore.firstname) {
      return [authStore.firstname, authStore.lastname].filter(Boolean).join(' ');
    }
    const email = userEmail.value;
    return email ? email.split('@')[0] : '';
  });
  const userInitials = computed(() => {
    let initials = '';
    if (authStore.firstname) {
      initials += authStore.firstname[0];
      if (authStore.lastname) {
        initials += authStore.lastname[0];
      }
      return initials.toUpperCase();
    }
    const email = userEmail.value;
    if (!email) return '?';
    const name = email.split('@')[0];
    return name
      .split(/[._-]/)
      .map((part) => part[0])
      .join('')
      .toUpperCase()
      .substring(0, 2);
  });

  const hasOrHadSubscription = computed(() => authStore.hasOrHadSubscription);
  const currentRoleLabel = computed(() => {
    const role = profile.value.market_role || 'none';
    const preset = rolePresets.value.find((item) => item.role === role);
    if (preset?.label) return preset.label;
    return t(`role_label_${role}`, role);
  });

  // Bug report (internal users only) — moved here from the sidebar footer so
  // it stays available but tucked away in the profile menu.
  const bugReportFormUrl =
    'https://profiteia.atlassian.net/jira/software/form/4366071e-25a2-427d-be3a-ebdac52299f2?atlOrigin=eyJpIjoiMjEyMTJjNzVjOWU2NDRkZmFlZmMwZDkwOTkzNTU4YWMiLCJwIjoiaiJ9';
  const bugReportAllowedDomains = ['@profiteia.io', '@bakeable.nl', '@flinknederland.nl'];
  const showBugReportButton = computed(() => {
    const email = userEmail.value;
    if (!email) return false;
    const normalizedEmail = email.toLowerCase();
    return bugReportAllowedDomains.some((domain) => normalizedEmail.endsWith(domain));
  });

  function openBugReportForm() {
    window.open(bugReportFormUrl, '_blank', 'noopener,noreferrer');
    closeDropdown();
  }

  // i18n Inspector (superuser only)
  const { isInspectMode, isSuperUser, toggleInspectMode } = useI18nInspector();

  function handleToggleInspect() {
    toggleInspectMode();
    closeDropdown();
  }

  // Dark mode
  const isDarkMode = computed(() => displayStore.darkMode);

  // Language
  const languageFlags = {
    nl: '🇳🇱',
    en: '🇬🇧',
    de: '🇩🇪',
    fr: '🇫🇷',
  };

  const languageNames = computed(() => ({
    nl: t('language.nl'),
    en: t('language.en'),
    de: t('language.de'),
    fr: t('language.fr'),
  }));

  const currentFlag = computed(() => languageFlags[locale.value] || '🌐');
  const currentLanguageName = computed(() => languageNames.value[locale.value] || locale.value);

  const availableLanguages = computed(() =>
    Object.keys(languageFlags)
      .filter((code) => code !== locale.value)
      .map((code) => ({
        code,
        flag: languageFlags[code],
        name: languageNames.value[code],
      }))
  );

  // Page title — used for the chrome's left-hand label. Individual pages own
  // their richer in-page header (with favoritable star, eyebrow, etc.) via
  // `StandardToolPage` / `PageLayout`, so the chrome no longer renders
  // breadcrumbs or the favoritable star.
  const pageTitle = computed(() => route.meta?.title || 'Profiteia');

  // Functions
  function toggleDropdown() {
    isDropdownOpen.value = !isDropdownOpen.value;
    if (!isDropdownOpen.value) {
      isLanguageSubmenuOpen.value = false;
    }
  }

  function closeDropdown() {
    isDropdownOpen.value = false;
    isLanguageSubmenuOpen.value = false;
  }

  function toggleLanguageSubmenu() {
    isLanguageSubmenuOpen.value = !isLanguageSubmenuOpen.value;
  }

  function setLocale(newLocale) {
    displayStore.setLocale(newLocale);
    isLanguageSubmenuOpen.value = false;
    closeDropdown();
  }

  function toggleDarkMode() {
    displayStore.toggleThemeMode();
  }

  async function logout() {
    await authStore.logoutUser();
    closeDropdown();
    redirectToLoginPage();
  }

  function toggleSidebar() {
    displayStore.setSidebarOpen(!displayStore.isSidebarOpen);
  }

  function checkMobileView() {
    isMobileView.value = window.innerWidth < 1024;
  }

  // Click outside to close
  function handleClickOutside(event) {
    if (dropdownRef.value && !dropdownRef.value.contains(event.target)) {
      closeDropdown();
    }
  }

  onMounted(() => {
    checkMobileView();
    window.addEventListener('resize', checkMobileView);
    document.addEventListener('click', handleClickOutside);
  });

  onUnmounted(() => {
    window.removeEventListener('resize', checkMobileView);
    document.removeEventListener('click', handleClickOutside);
  });
</script>

<style scoped>
  /* Dropdown transitions */
  .dropdown-enter-active,
  .dropdown-leave-active {
    transition: all 0.2s ease;
  }

  .dropdown-enter-from,
  .dropdown-leave-to {
    opacity: 0;
    transform: translateY(-10px);
  }

  /* Submenu transitions */
  .submenu-enter-active,
  .submenu-leave-active {
    transition: all 0.2s ease;
    overflow: hidden;
  }

  .submenu-enter-from,
  .submenu-leave-to {
    max-height: 0;
    opacity: 0;
  }

  .submenu-enter-to,
  .submenu-leave-from {
    max-height: 200px;
    opacity: 1;
  }

  /* Icon swap animation */
  .icon-swap-enter-active,
  .icon-swap-leave-active {
    transition: all 0.2s ease;
  }

  .icon-swap-enter-from {
    opacity: 0;
    transform: rotate(-45deg) scale(0.8);
  }

  .icon-swap-leave-to {
    opacity: 0;
    transform: rotate(45deg) scale(0.8);
  }
</style>
